import React from 'react';
import { BulkUpdateLabelAction } from '../../../../../common/src/domain/survey/survey/surveyPaths';
import { RequestBody } from '../../../../../common/src/routes/openApiTypes';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchPost } from '../../../store/openApiFetch';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import { ModalForm } from '../../common/Modal/ModalForm';
import {
    EditPanel,
    EditPanelRow,
    EditPanelCell,
    EditPanelSwitch,
    EditPanelFilterSelect,
    EditPanelFilterCreateSelect,
    Alert,
    InputRadioButtons,
} from '../../elements';

type SurveyListBulkUpdateModalProps = {
    itemCount: number;
    surveyStatusOptions: { value: string; text: string }[];
    surveySearchQuery: Record<string, unknown>;
    reload(): void;
    onClose(): void;
};

type SurveyListBulkUpdateModalState = {
    shouldUpdateStatus: boolean;
    status: string;
    shouldUpdateLabels: boolean;
    labels: string[];
    hasAcceptedLargeBulkUpdate: boolean;
    labelUpdateAction: BulkUpdateLabelAction;
};

const numberOfSurveysToAcceptLargeBulkUpdate = 50;

export function SurveyListBulkUpdateModal(props: SurveyListBulkUpdateModalProps) {
    const translate = useTranslate();
    const [state, setState] = React.useState<SurveyListBulkUpdateModalState>({
        shouldUpdateStatus: false,
        status: props.surveyStatusOptions[0]!.value,
        shouldUpdateLabels: false,
        labels: [],
        hasAcceptedLargeBulkUpdate: false,
        labelUpdateAction: 'add',
    });
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const { data: knownLabels } = useOpenApiFetch('/api/surveys/knownLabels', 'get', {});

    async function update() {
        setErrorMessage(null);
        const body: RequestBody<'/api/surveys/bulkUpdate', 'post'> = {};
        if (state.shouldUpdateLabels) {
            body.labels = state.labels;
            body.labelAction = state.labelUpdateAction;
        }
        if (state.shouldUpdateStatus) {
            body.status = state.status;
        }

        try {
            await openApiFetchPost('/api/surveys/bulkUpdate', props.surveySearchQuery, body);
            props.reload();
            props.onClose();
        } catch (e) {
            setErrorMessage(e.message);
        }
    }

    function canUpdate() {
        if (props.itemCount === 0) {
            return false;
        }
        if (props.itemCount > numberOfSurveysToAcceptLargeBulkUpdate && !state.hasAcceptedLargeBulkUpdate) {
            return false;
        }
        return state.shouldUpdateStatus || state.shouldUpdateLabels;
    }

    return (
        <ModalForm
            onClose={() => props.onClose()}
            title={translate('survey.bulkUpdateSurveys', props.itemCount.toString())}
            cancelButton={{ onClick: () => props.onClose() }}
            saveButton={{ onClick: update, disabled: !canUpdate(), textOverride: translate('common.update') }}
        >
            {errorMessage && <Alert severity="danger">{errorMessage}</Alert>}
            {translate('survey.bulkUpdateExplanation')}
            <EditPanel>
                <EditPanelRow>
                    <EditPanelCell rowCount={3}>
                        <EditPanelSwitch
                            placeholder={translate('common.status')}
                            name="shouldUpdateStatus"
                            value={state.shouldUpdateStatus}
                            handleChange={() => setState((prev) => ({ ...prev, shouldUpdateStatus: !prev.shouldUpdateStatus }))}
                        />
                    </EditPanelCell>

                    <EditPanelCell rowCount={6}>
                        {state.shouldUpdateStatus && (
                            <div className="mt10">
                                <EditPanelFilterSelect
                                    name="status"
                                    value={state.status}
                                    handleChange={(value) => setState((prev) => ({ ...prev, status: value }))}
                                    placeholder={translate('common.selectItem', translate('common.status'))}
                                    options={props.surveyStatusOptions}
                                    disabled={!state.shouldUpdateStatus}
                                />
                            </div>
                        )}
                    </EditPanelCell>
                </EditPanelRow>

                <EditPanelRow>
                    <EditPanelCell rowCount={3}>
                        <EditPanelSwitch
                            placeholder={translate('common.labels')}
                            name="shouldUpdateLabels"
                            value={state.shouldUpdateLabels}
                            handleChange={() => setState((prev) => ({ ...prev, shouldUpdateLabels: !prev.shouldUpdateLabels }))}
                        />
                    </EditPanelCell>
                    <EditPanelCell rowCount={6}>
                        {state.shouldUpdateLabels && (
                            <>
                                <div className="mt10">
                                    <EditPanelFilterCreateSelect
                                        name="labels"
                                        placeholder={translate('common.selectItem', translate('common.labels'))}
                                        options={knownLabels ?? []}
                                        value={state.labels}
                                        handleChange={(value) => setState((prev) => ({ ...prev, labels: value }))}
                                        disabled={!state.shouldUpdateLabels}
                                    />
                                </div>
                            </>
                        )}
                    </EditPanelCell>
                </EditPanelRow>
                {state.shouldUpdateLabels && (
                    <EditPanelRow>
                        <EditPanelCell rowCount={3}></EditPanelCell>
                        <EditPanelCell rowCount={6}>
                            <InputRadioButtons
                                options={[
                                    { value: 'add', text: translate('survey.labelUpdateActionAdd') },
                                    { value: 'replace', text: translate('survey.labelUpdateActionReplace') },
                                ]}
                                name="labelUpdateAction"
                                value={state.labelUpdateAction}
                                handleChange={(value) => setState((prev) => ({ ...prev, labelUpdateAction: value as BulkUpdateLabelAction }))}
                            />
                        </EditPanelCell>
                    </EditPanelRow>
                )}
                {props.itemCount > numberOfSurveysToAcceptLargeBulkUpdate && (
                    <EditPanelRow>
                        <EditPanelCell rowCount={12}>
                            <EditPanelSwitch
                                placeholder={translate('survey.bulkUpdateAcceptLargeUpdate', props.itemCount.toString())}
                                name="hasAcceptedLargeBulkUpdate"
                                value={state.hasAcceptedLargeBulkUpdate}
                                handleChange={() => setState((prev) => ({ ...prev, hasAcceptedLargeBulkUpdate: !prev.hasAcceptedLargeBulkUpdate }))}
                                offText={translate('common.no')}
                                onText={translate('common.yes')}
                            />
                        </EditPanelCell>
                    </EditPanelRow>
                )}
            </EditPanel>
        </ModalForm>
    );
}
